/**
 * The "Today" grid behind the list choice (19/10/2026): which of the
 * group's lists each of today's drops goes to — one row per list (#1 best
 * first, the rest folded into a "+N more" row), one column per drop. The
 * same rules as the planner: one list → #1; every list → all of them;
 * alternate → the next in turn (a drop tied to a rank takes no turn); a
 * rank → that list, or none when today has fewer lists.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { ListMode } from './list-mode';

export const SHOWN_ROWS = 3;
export const SHOWN_COLS = 6;

export interface MatrixDrop {
  label: string;
  /** The drop is tied to this rank of the group's lists (all lists only). */
  rank?: number;
}

/** The list indexes (0 = the best) each drop goes to, in drop order. */
export function dropTargets(mode: ListMode, drops: MatrixDrop[], lists: number): number[][] {
  let turn = 0;
  return drops.map((drop) => {
    if (lists <= 0) return [];
    if (mode !== 'one' && drop.rank !== undefined) return drop.rank <= lists ? [drop.rank - 1] : [];
    if (mode === 'one') return [0];
    if (mode === 'every') return Array.from({ length: lists }, (_, i) => i);
    return [turn++ % lists];
  });
}

/** A target as a shown row: its own row, or the "+N more" row (SHOWN_ROWS) past the shown ones. */
export const shownRow = (index: number): number => Math.min(index, SHOWN_ROWS);

/** The sentence under the grid — the day in real numbers (ranked = drops tied to a rank). */
export function matrixSummary(mode: ListMode, drops: number, lists: number, ranked = 0): string {
  const d = `${drops} drop${drops === 1 ? '' : 's'}`;
  if (lists <= 0) return 'No list matches this group today — its drops send nothing.';
  if (drops === 0) return 'No drop of this group is planned today.';
  if (mode === 'one') return `${d} today, all to the best list.`;
  if (ranked === drops) return `${d}, each tied to its own rank — ${drops >= lists ? `all ${lists} lists get one today` : `lists #1–#${drops} get one each, #${drops + 1}–#${lists} none today`}.`;
  if (mode === 'every') return `${d} × ${lists} lists = ${(drops * lists).toLocaleString('en-US')} sends today.`;
  if (drops >= lists) {
    const low = Math.floor(drops / lists);
    const high = Math.ceil(drops / lists);
    return `${d} take turns over ${lists} lists — each list gets ${low === high ? low : `${low}–${high}`} today.`;
  }
  return `${d} take turns — lists #1–#${drops} get one each, #${drops + 1}–#${lists} none today.`;
}
